import type { Area } from "./areas";
import type { ShopSection } from "./shop";

export interface WorkspaceNavigation {
  area: Area;
  blogSection: "posts" | "tags" | "settings";
  blogPostId: string | null;
  shopSection: ShopSection;
  shopProductId: string | null;
  shopOrderId: string | null;
  pagePath: string | null;
}

export const DEFAULT_NAVIGATION: WorkspaceNavigation = {
  area: "website", blogSection: "posts", blogPostId: null,
  shopSection: "products", shopProductId: null, shopOrderId: null, pagePath: null,
};

/** Unknown paths are old site-id bookmarks or invalid destinations; use the Website. */
export function readWorkspaceNavigation(pathname: string, search = ""): WorkspaceNavigation {
  const result = { ...DEFAULT_NAVIGATION };
  const query = new URLSearchParams(search);
  const parts = pathname.replace(/^\/builder\/?/, "").split("/").filter(Boolean);
  if (!parts.length && query.has("shop-connect")) return { ...result, area: "shop", shopSection: "settings" };
  const [area, section, encodedId] = parts;
  if (area === "website" && parts.length === 1) {
    const page = query.get("page");
    result.pagePath = page?.startsWith("/") && !page.startsWith("//") ? page : null;
  } else if (area === "blog" && parts.length <= 3 && (!section || ["posts", "tags", "settings"].includes(section)) && (!encodedId || section === "posts")) {
    result.area = "blog";
    result.blogSection = (section ?? "posts") as WorkspaceNavigation["blogSection"];
    try { result.blogPostId = encodedId ? decodeURIComponent(encodedId) : null; } catch { return { ...DEFAULT_NAVIGATION }; }
  } else if (area === "shop" && parts.length <= 3 && (!section || ["products", "categories", "orders", "settings"].includes(section)) && (!encodedId || section === "products" || section === "orders")) {
    result.area = "shop";
    result.shopSection = (section ?? "products") as ShopSection;
    try {
      const id = encodedId ? decodeURIComponent(encodedId) : null;
      if (section === "orders") result.shopOrderId = id;
      else result.shopProductId = id;
    } catch { return { ...DEFAULT_NAVIGATION }; }
  }
  if (result.area === "website" && area !== "website" && query.has("shop-connect")) return { ...result, area: "shop", shopSection: "settings" };
  return result;
}

export function workspaceNavigationPath(value: WorkspaceNavigation): string {
  if (value.area === "website") return "/builder/website";
  if (value.area === "blog") return `/builder/blog/${value.blogSection}${value.blogSection === "posts" && value.blogPostId ? `/${encodeURIComponent(value.blogPostId)}` : ""}`;
  const id = value.shopSection === "products" ? value.shopProductId : value.shopSection === "orders" ? value.shopOrderId : null;
  return `/builder/shop/${value.shopSection}${id ? `/${encodeURIComponent(id)}` : ""}`;
}

export function workspaceNavigationHref(value: WorkspaceNavigation, search = "", hash = ""): string {
  const query = new URLSearchParams(search);
  query.delete("page");
  if (value.area === "website" && value.pagePath) query.set("page", value.pagePath);
  const params = query.toString();
  return `${workspaceNavigationPath(value)}${params ? `?${params}` : ""}${hash ? `#${hash.replace(/^#/, "")}` : ""}`;
}

export interface WorkspaceNavigationController {
  removeSearchParameter?: (name: string) => void;
  value: WorkspaceNavigation;
  update: (change: Partial<WorkspaceNavigation>, replace?: boolean) => void;
}
